import React from 'react';
import { Box } from '@mui/material';
import clsx from 'clsx';
import '../styles/AudioLoader.css';

interface AudioVisualizerProps {
  isPlaying: boolean;
  variant?: 'wave' | 'bars' | 'circle';
  size?: 'small' | 'medium' | 'large';
  color?: string;
  className?: string;
}

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  isPlaying,
  variant = 'wave',
  size = 'small',
  color = '#3f51b5',
  className,
}) => {
  if (!isPlaying) return null;

  // Size mappings
  const sizeMap = {
    small: {
      height: 12,
      width: 2,
      gap: 1,
      containerClass: 'h-3',
    },
    medium: {
      height: 16,
      width: 3,
      gap: 2,
      containerClass: 'h-4',
    },
    large: {
      height: 20,
      width: 4,
      gap: 3,
      containerClass: 'h-5',
    },
  };

  const currentSize = sizeMap[size];

  // Wave variant
  if (variant === 'wave') {
    return (
      <Box
        className={clsx('audio-wave flex items-center', currentSize.containerClass, className)}
        sx={{ height: `${currentSize.height}px` }}
      >
        {[...Array(5)].map((_, i) => (
          <span
            key={i}
            className="audio-wave-bar"
            style={{
              width: `${currentSize.width}px`,
              margin: `0 ${currentSize.gap}px`,
              backgroundColor: color,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </Box>
    );
  }

  // Bars variant
  if (variant === 'bars') {
    return (
      <Box
        className={clsx('audio-bars flex items-center', currentSize.containerClass, className)}
        sx={{ height: `${currentSize.height}px` }}
      >
        {[...Array(4)].map((_, i) => (
          <span
            key={i}
            className="audio-bar"
            style={{
              width: `${currentSize.width}px`,
              margin: `0 ${currentSize.gap}px`,
              height: '100%',
              backgroundColor: color,
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
      </Box>
    );
  }

  // Circle variant (pulsing dot)
  return (
    <Box className={clsx('audio-circle flex items-center justify-center', className)}>
      <span
        className="audio-circle-dot"
        style={{
          width: `${currentSize.height / 2}px`,
          height: `${currentSize.height / 2}px`,
          backgroundColor: color,
          borderRadius: '50%',
        }}
      />
    </Box>
  );
};

export default AudioVisualizer;
